import { useState, useEffect } from 'react'
import moment from 'moment'

import { getEdaStatus, createNewEda, removeEDACheckStatus, changeEdaBankAccount } from 'e-submission/apps/actions'
import {
  isEDACheckingStatus,
  isEDACheckingStatusError,
  isEDACheckingStatusExpired,
  getCurrentApp,
} from 'e-submission/apps/selectors'
import { getIcon } from 'e-submission/components/Icon'
import { getATPBankText, getPolicyId, getRecurringPayment } from 'e-submission/domain/data-model/form/selectors'
import { get } from 'lodash/fp'
import { QRCodeCanvas } from 'qrcode.react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Flex } from 'rebass'
import styled from 'styled-components'
import ktaxaLogo from '!url-loader!assets/images/logo-ktaxa-th-bordered.png'

const QRWrapper = styled(({ show, ...props }) => <Flex {...props} />)`
  opacity: ${({ show }) => (show ? 1 : 0)};
  margin-bottom: '20px';
`

// ─── canvas helpers ──────────────────────────────────────────────────────────

function drawRoundRect(ctx, x, y, w, h, r, fillColor, strokeColor, strokeWidth) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
  if (fillColor) {
    ctx.fillStyle = fillColor
    ctx.fill()
  }
  if (strokeColor) {
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth || 2
    ctx.stroke()
  }
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(' ')
  const lines = []
  let line = ''
  for (const word of words) {
    const test = line ? line + ' ' + word : word
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line)
      line = word
    } else {
      line = test
    }
  }
  if (line) lines.push(line)
  return lines
}

function generateQRCardCanvas(qrCanvas, expiryDateTime) {
  const SCALE = 2
  const W = 840
  const BG_COLOR = '#2222B5'
  const THAI_FONT = '"helvethaica", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

  const CARD_MX = 200
  const CARD_MY = 70
  const CARD_W = W - CARD_MX * 2

  const TITLE_H = 90
  const QR_SIZE = 280
  const QR_MT = 40
  const BOX_MT = 36
  const BOX_MX = 24
  const BOX_PAD_V = 22
  const LINE_H = 26
  const CARD_PB = 44

  const measureCtx = document.createElement('canvas').getContext('2d')
  const INFO_FONT = `18px ${THAI_FONT}`
  measureCtx.font = INFO_FONT
  const expiryText = moment(expiryDateTime).format('DD/MM/YYYY เวลา HH:mm น.')
  const infoText =
    `กรุณาสแกน QR Code เพื่อดำเนินการหักค่าเบี้ยประกันอัตโนมัติ` + ` ผ่านบัญชีธนาคารกรุงศรี ภายในวันที่ ${expiryText}`
  const TEXT_MAX_W = CARD_W - BOX_MX * 2 - 50 - 10
  const infoLines = wrapLines(measureCtx, infoText, TEXT_MAX_W)
  const BOX_H = infoLines.length * LINE_H + BOX_PAD_V * 2

  const CARD_H = TITLE_H + QR_MT + QR_SIZE + BOX_MT + BOX_H + CARD_PB
  const H = CARD_MY + CARD_H + CARD_MY

  const offscreen = document.createElement('canvas')
  offscreen.width = W * SCALE
  offscreen.height = H * SCALE
  const ctx = offscreen.getContext('2d')
  ctx.scale(SCALE, SCALE)

  ctx.fillStyle = BG_COLOR
  ctx.fillRect(0, 0, W, H)

  drawRoundRect(ctx, CARD_MX, CARD_MY, CARD_W, CARD_H, 18, '#ffffff')

  ctx.fillStyle = '#2222B5'
  ctx.font = `bold 20px ${THAI_FONT}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('สแกน QR Code เพื่อดำเนินการสมัคร', W / 2, CARD_MY + 44)

  ctx.strokeStyle = '#e0e0e0'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(CARD_MX + 20, CARD_MY + TITLE_H - 4)
  ctx.lineTo(CARD_MX + CARD_W - 20, CARD_MY + TITLE_H - 4)
  ctx.stroke()

  const qrX = (W - QR_SIZE) / 2
  const qrY = CARD_MY + TITLE_H + QR_MT
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(qrCanvas, qrX, qrY, QR_SIZE, QR_SIZE)
  // re-enable smoothing for the rest of the drawing (text, arcs)
  ctx.imageSmoothingEnabled = true

  const boxX = CARD_MX + BOX_MX
  const boxY = qrY + QR_SIZE + BOX_MT
  const boxW = CARD_W - BOX_MX * 2
  drawRoundRect(ctx, boxX, boxY, boxW, BOX_H, 8, '#EEF1FF', '#4040CC', 1.5)

  // Info icon — draw on a higher-res sub-canvas then blit to avoid text blur
  const ICON_R = 16
  const iconCX = boxX + 28
  const iconCY = boxY + BOX_PAD_V + LINE_H / 2
  const iconScale = 4
  const iconDia = ICON_R * 2 * iconScale
  const iconOff = document.createElement('canvas')
  iconOff.width = iconDia
  iconOff.height = iconDia
  const ic = iconOff.getContext('2d')
  ic.beginPath()
  ic.arc(iconDia / 2, iconDia / 2, (ICON_R - 1) * iconScale, 0, Math.PI * 2)
  ic.fillStyle = '#3535CC'
  ic.fill()
  ic.fillStyle = '#ffffff'
  ic.font = `bold ${14 * iconScale}px Georgia, serif`
  ic.textAlign = 'center'
  ic.textBaseline = 'middle'
  ic.fillText('i', iconDia / 2, iconDia / 2 + iconScale)
  ctx.drawImage(iconOff, iconCX - ICON_R, iconCY - ICON_R, ICON_R * 2, ICON_R * 2)

  ctx.fillStyle = '#3535CC'
  ctx.font = INFO_FONT
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  infoLines.forEach((line, i) => {
    ctx.fillText(line, boxX + 50, boxY + BOX_PAD_V + i * LINE_H)
  })

  return offscreen
}

// ─── download ────────────────────────────────────────────────────────────────

function saveBase64ImageWeb(options) {
  const { data, prefix, format } = options
  const ext = (format || 'JPG').toLowerCase() === 'png' ? 'png' : 'jpeg'
  const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg'

  const base64 = data.replace(/^data:image\/\w+;base64,/, '')

  const timestamp = new Date()
    .toISOString()
    .replace(/[-T:.Z]/g, '')
    .slice(0, 14)
  const fileName = `${prefix || 'img_'}${timestamp}.${ext}`

  // Use Blob + createObjectURL for Safari compatibility
  // (<a download> with data URI is not supported in Safari)
  try {
    const byteString = atob(base64)
    const byteArray = new Uint8Array(byteString.length)
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i)
    }
    const blob = new Blob([byteArray], { type: mimeType })
    const blobUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = fileName
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  } catch (e) {
    // fallback: direct data URI for legacy browsers
    const anchor = document.createElement('a')
    anchor.href = `data:${mimeType};base64,${base64}`
    anchor.download = fileName
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }
}

function onClickDownload(expiryDateTime, setIsDownloading) {
  const qrCanvas = document.getElementsByTagName('canvas')[0]
  if (!qrCanvas) {
    console.error('QR Code canvas not found')
    return
  }

  setIsDownloading(true)

  const MIN_LOADING_MS = 3000

  // defer heavy work so React re-renders the loading state first
  setTimeout(() => {
    const startTime = Date.now()
    const finishLoading = () => {
      const elapsed = Date.now() - startTime
      const remaining = MIN_LOADING_MS - elapsed
      setTimeout(() => setIsDownloading(false), remaining > 0 ? remaining : 0)
    }

    try {
      const cardCanvas = generateQRCardCanvas(qrCanvas, expiryDateTime)
      const imageUrl = cardCanvas.toDataURL('image/jpeg', 0.92)
      const params = {
        data: imageUrl,
        prefix: 'eda_qr_code_',
        format: 'JPG',
        quality: 90,
        mediaScanner: true,
      }

      if (window.imageSaver) {
        // Android & iOS Cordova — async, done in callbacks
        window.imageSaver.saveBase64Image(
          params,
          function(filePath) {
            console.log('File saved on ' + filePath)
            finishLoading()
          },
          function(msg) {
            console.error(msg)
            finishLoading()
          }
        )
      } else {
        // Web browser — synchronous
        saveBase64ImageWeb(params)
        finishLoading()
      }
    } catch (e) {
      console.error(e)
      finishLoading()
    }
  }, 0)
}

const QrCodePage = (props) => {
  const { paymentTokenRegistrationURL, expiryDateTime, isLoading } = props
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    // You can add lifecycle logic here if needed, replacing the old `lifecycle` HOC.
  }, [])

  return (
    <>
      {isLoading && (
        <div className="spinner-overlay">
          <div className="spinner-eda"></div>
        </div>
      )}
      <div className="eda-check-status">
        <div className="content-container" style={{ borderRadius: '10px', marginBottom: '30px' }}>
          <QRWrapper id="paymentQrWrapper" show={true} mt={30} justify="center">
            <QRCodeCanvas
              value={paymentTokenRegistrationURL}
              size={200}
              bgColor={'#ffffff'}
              fgColor={'#000000'}
              level={'H'}
              includeMargin={false}
              imageSettings={{
                src: `${ktaxaLogo}`,
                x: undefined,
                y: undefined,
                height: 30,
                width: 60,
                excavate: true,
              }}
            />
          </QRWrapper>
          <div className="info-message">
            <br />
          </div>
          <div className="info-message attached-icon form-control-feedback">
            <p>
              กรุณาสแกน QR Code เพื่อดำเนินการหักค่าเบี้ยประกันอัตโนมัติ ผ่านบัญชีธนาคารกรุงศรี ภายในวันที่{' '}
              {moment(expiryDateTime).format('DD/MM/YYYY เวลา HH:mm น.')}
            </p>
          </div>
        </div>
        <div
          className="sub-heading sub-heading-bank-text sub-heading-qr-text"
          onClick={() => !isDownloading && onClickDownload(expiryDateTime, setIsDownloading)}
          style={{ opacity: isDownloading ? 0.5 : 1, cursor: isDownloading ? 'not-allowed' : 'pointer' }}
        >
          <div style={{ height: '48px' }} className="icon vertical-align-middle">
            {isDownloading ? <div className="spinner-eda-downloadqrcode" /> : getIcon('downloadDocumentRounded')}
          </div>{' '}
          <span style={{ marginLeft: '30px' }} className={'vertical-align-middle'}>
            {isDownloading ? 'กำลังบันทึก...' : 'บันทึก QR Code'}
          </span>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  const isLoading = isEDACheckingStatus(state)
  const isStatusError = isEDACheckingStatusError(state)
  const isStatusExpired = isEDACheckingStatusExpired(state)

  const app = getCurrentApp(state)
  const recurringPayment = getRecurringPayment(app)
  const policyId = getPolicyId(app)
  const atpBankText = getATPBankText(app)
  const accountNo = get('bankBookNumber', recurringPayment)

  return {
    isLoading,
    isStatusError,
    isStatusExpired,
    policyId,
    accountNo,
    atpBankText,
    paymentTokenRegistrationURL: app.ATP.paymentTokenRegistrationURL,
    expiryDateTime: app.ATP.expiryDateTime || new Date().toUTCString(),
  }
}
const mapDispatchToProps = (dispatch) => ({
  onClickCheckStatus: (toggleModal) => {
    dispatch(getEdaStatus(toggleModal))
  },
  onClickResendForm: (policyId, accountNo) => {
    const payload = {
      accountNo: accountNo,
      policyNo: policyId,
      bankName: 'KTB', // only KTB for now
      applyDtm: new Date().getTime(),
    }
    dispatch(createNewEda(payload))
  },
  unsetCheckStatusEDA: () => {
    dispatch(removeEDACheckStatus())
  },
  changeBankAccount: () => {
    dispatch(changeEdaBankAccount())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(QrCodePage)
